import { NonUndefined } from "./types";

export function groupBy<T, X>(ts: T[], fn: (t: T) => X): Map<X, T[]> {
  const map = new Map<X, T[]>();
  ts.forEach((t) => {
    const key = fn(t);
    const collection = map.get(key);
    if (!collection) {
      map.set(key, [t]);
    } else {
      collection.push(t);
    }
  });
  return map;
}

export function collect<T, U>(ts: T[], fn: (t: T) => U | undefined): U[] {
  const us: U[] = [];
  ts.forEach((t) => {
    const result = fn(t);
    if (result) us.push(result);
  });
  return us;
}

export function collectFirst<T, U>(ts: T[], fn: (t: T) => U | undefined): U | undefined {
  for (let i = 0; i < ts.length; i++) {
    const t = ts[i];
    if (t === undefined) continue;
    const result = fn(t);
    if (result) return result;
  }
  return undefined;
}

export function fold<T, U>(ts: T[], initial: U, step: (u: U, t: T) => U): U {
  let res = initial;
  ts.forEach((t) => {
    res = step(res, t);
  });
  return res;
}

export function stripNull<T>(t: T | null | undefined): T | undefined {
  return t === null ? undefined : t;
}

export function stripNullEmpty(t: string | null | undefined): string | undefined {
  return t === null || t === "" ? undefined : t;
}

export function exists<T>(ts: T[], fn: (t: T) => boolean): boolean {
  for (const t of ts) {
    if (fn(t)) return true;
  }
  return false;
}

export function empty<T>(ts: T[] | string | undefined | null): boolean {
  if (ts === undefined || ts === null) return true;
  else return ts.length === 0;
}

export function nonEmpty<T>(ts: T[] | string | undefined | null): boolean {
  if (ts === undefined || ts === null) return false;
  else return ts.length !== 0;
}

export function filterNonEmpty<T>(ts: T[] | null | undefined): T[] | undefined {
  if (ts === null || ts === undefined || empty(ts)) return undefined;
  else return ts;
}

export function contains<T>(ts: T[] | undefined, t: T): boolean {
  if (ts === undefined) return false;
  else return ts.indexOf(t) !== -1;
}

export function stringContains(s: string | undefined, v: string): boolean {
  if (s === undefined) return false;
  else return s.indexOf(v) !== -1;
}

export function containsEq<T>(ts: T[], t: T, eq: (t1: T, t2: T) => boolean): boolean {
  return ts.find((e) => eq(e, t)) !== undefined;
}

export function duplicates<T>(ts: T[], eq: (t1: T, t2: T) => boolean): [T, number][] {
  const seen: T[] = [];
  const dupes: [T, number][] = [];
  ts.forEach((t, idx) => {
    const isSeen = Boolean(seen.find((r) => eq(t, r)));
    if (isSeen) dupes.push([t, idx]);
    else seen.push(t);
  });
  return dupes;
}

export function distinct<T>(ts: T[], eq?: (t1: T, t2: T) => boolean): T[] {
  const deduped: T[] = [];
  ts.forEach((t) => {
    const isSeen = Boolean(deduped.find((r) => (eq ? eq(t, r) : t === r)));
    if (!isSeen) {
      deduped.push(t);
    }
  });
  return deduped;
}

export function flatten<T>(ts: Array<T | undefined | null>): T[] {
  return ts.filter((t) => t !== undefined && t !== null) as T[];
}

export function zipWithIndex<T>(ts: T[]): Array<[T, number]> {
  return ts.map((t, idx) => [t, idx]);
}

export function updateElement<T>(ts: T[], pred: (t: T) => boolean, update: (t: T) => T): T[] {
  return ts.map((t) => {
    if (pred(t)) {
      return update(t);
    } else return t;
  });
}

export function min<T>(ts: T[], compareFn: (t1: T, t2: T) => number): T {
  return ts.reduce((t1, t2) => {
    return compareFn(t1, t2) <= 0 ? t1 : t2;
  });
}

export function maxBy<T>(ts: T[], compareFn: (t1: T, t2: T) => number): T {
  return ts.reduce((t1, t2) => {
    return compareFn(t1, t2) > 0 ? t1 : t2;
  });
}

export function swapImmut<T>(ts: NonUndefined<T>[], idx1: number, idx2: number): NonUndefined<T>[] {
  const firstItem = ts[idx1];
  const secondItem = ts[idx2];

  if (firstItem === undefined) throw new Error("swapImmut: first index out of bounds");
  if (secondItem === undefined) throw new Error("swapImmut: second index out of bounds");

  const result = ts.slice();
  result[idx1] = secondItem;
  result[idx2] = firstItem;
  return result;
}

export function sortByKeys(
  obj: { [key: string]: any },
  sortFn: (a: string, b: string) => number
): { [key: string]: any } {
  const keys = Object.keys(obj);
  keys.sort(sortFn);
  const result = {};
  keys.forEach((key) => {
    result[key] = obj[key];
  });
  return result;
}

export function setEquals<T>(a: Set<T>, b: Set<T>): boolean {
  if (a.size !== b.size) return false;
  for (const elem of a) {
    if (!b.has(elem)) return false;
  }
  return true;
}

export function pluralize(count: number, singular: string, plural: string = `${singular}s`): string {
  return count === 1 ? singular : plural;
}
